import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import ReactMarkdown from "react-markdown";
import { useCommonTranslations } from "../components/CommonTranslations";
import Layout from "../components/layout";
import Seo from "../components/seo";
import { StayInTouch } from "../components/StayinTouch";
import { useFilteredByLocale } from "../hooks";

const firebaseConfig = {
  apiKey: "AIzaSyD-KLsrwvTJpq_fNRsqmzoi1R682vuTWk4",
  authDomain: "firebird-8a726.firebaseapp.com",
  projectId: "firebird-8a726",
  storageBucket: "firebird-8a726.appspot.com",
  messagingSenderId: "247970633058",
  appId: "1:247970633058:web:1e5792454fd18e9dbf083b",
};

const firebaseApp = initializeApp(firebaseConfig);

export const db = getFirestore();

export default function PressAndContact({ location }: { location: Location }) {
  const commonTranslations = useCommonTranslations();
  return (
    <Layout location={location} headerProps={{ fixed: true, filled: true, logo: true }}>
      <Seo title={commonTranslations.pressAreaAndContact} />
      <Body />
    </Layout>
  );
}

const Body = () => {
  const { contactEmails, helpCenterTitle, helpCenterBody } = usePressAndContact();

  return (
    <div className="min-h-screen w-full bg-black px-5 pb-20 pt-28 text-white press-and-contacts md:px-10 md:pt-36">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-10">
        <section className="grid gap-8 md:grid-cols-2 md:items-start">
          <div className={sectionCardClassName}>
            <StayInTouch form formClassName="flex flex-col space-y-6" />
          </div>
          <div className={sectionCardClassName}>
            <PressInquiries />
          </div>
        </section>

        <section className="grid gap-8 md:grid-cols-2 md:items-start">
          <div className={sectionCardClassName}>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">FAQ</p>
            <h1 className="mb-6 text-3xl font-bold md:text-4xl">{helpCenterTitle}</h1>
            <ReactMarkdown className="space-y-4 text-base font-medium leading-7 text-gray-200 md:text-lg">
              {helpCenterBody}
            </ReactMarkdown>
          </div>
          <div className={sectionCardClassName}>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">Contact</p>
            <div className="flex flex-col gap-3">
              {contactEmails.map(({ email, title }) => (
                <EmailCard key={`${title}-${email}`} email={email} info={title} />
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const sectionCardClassName =
  "rounded-2xl border border-white border-opacity-10 bg-white bg-opacity-[0.03] p-6 shadow-2xl md:p-8";

const innerPanelClassName = "rounded-xl border border-white border-opacity-10 bg-black bg-opacity-45 p-4";

const PressInquiries = () => {
  const { pressInquiries, pressInquiriesSubtext, pressInquiryEmails } = usePressAndContact();
  return (
    <div>
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">Press</p>
      <h2 className="mb-5 text-3xl font-bold md:text-4xl">{pressInquiries}</h2>
      <h3 className="mb-4 text-base font-semibold leading-7 text-gray-200 md:text-lg">{pressInquiriesSubtext}</h3>
      <div className="flex flex-col gap-4">
        {pressInquiryEmails.map((props) => (
          <EmailCard key={`${props.info}-${props.email}`} {...props} />
        ))}
      </div>
    </div>
  );
};

interface EmailCardProps {
  info: string;
  email: string;
  name?: string;
}

const EmailCard = ({ info, email, name }: EmailCardProps) => {
  return (
    <a
      href={`mailto:${email}`}
      target="_blank"
      rel="noreferrer"
      className={`group flex flex-col transition hover:border-primary hover:bg-opacity-80 !no-underline ${innerPanelClassName}`}
    >
      <p className="pr-2 text-lg font-bold leading-6 md:text-xl">{info}</p>
      <div className="mt-3 flex flex-col gap-1 text-sm text-gray-300 sm:flex-row sm:flex-wrap sm:justify-between">
        <span className="break-all group-hover:text-white">{email}</span>
        {name && <span className="font-bold text-white">{name}</span>}
      </div>
    </a>
  );
};

const usePressAndContact = (): PressAndContact => {
  const {
    allStrapiPressAreaAndContact: { edges },
  } = useStaticQuery<{ allStrapiPressAreaAndContact: EdgesQuery<{ node: PressAndContact }> }>(query);

  return useFilteredByLocale(edges.map(({ node }) => node))[0];
};

const query = graphql`
  query PressAndContact {
    allStrapiPressAreaAndContact {
      edges {
        node {
          title
          subTitle
          pressInquiries
          pressInquiriesSubtext
          pressInquiryEmails {
            email
            info
            name
          }
          name
          pleaseFindOurPressMaterialsHere
          request
          send
          locale
          contactEmails {
            email
            title
          }
          helpCenterTitle
          helpCenterBody
        }
      }
    }
  }
`;
